// Leaf data module, not the `@store/fast-versions` barrel: the picker reads
// these rules too, and the barrel pulls RTK Query in behind a constant.
import { accountVariables } from "@store/fast-versions/data/account-variables";
import { FAST_VERSION_VARIABLES } from "@/providers/rendering-provider/data/default-variables";
import { STORYDOC_VARIABLES } from "../data/storydoc-variables";
import type { PlanTier } from "../variable-picker/types";

/**
 * Which plan a variable needs before a story using it can be published.
 *
 * Mirrored in dotell-backend `pages/variables/plan-rules.js` — the backend is
 * the real gate (version create/update), this copy powers the pre-flight
 * dialog. Keep the two in step; `plan-rules.test.ts` snapshots the catalogue
 * map so a catalogue edit shows up as a failing test here.
 *
 * Precedence:
 *  1. Legacy default variables and account/profile variables — free. They were
 *     never gated and are auto-filled from the sender's profile.
 *  2. Storydoc catalogue variables — the catalogue's own `plan`.
 *  3. Calculation variables — Team.
 *  4. Fathom variables — free: the Fathom integration isn't plan-gated
 *     anywhere (no subscription-limits entry; the tab shows for any org with
 *     it connected), so gating its variables would block existing users.
 *  5. Everything else (custom variables, profile custom fields, HubSpot —
 *     a Pro-and-up integration) — Pro.
 */

export type AccountPlanType = "trial" | "starter" | "pro" | "team";

export interface PlanRuleVariable {
  name: string;
  type?: string;
}

export interface BlockedVariable {
  name: string;
  requiredPlan: PlanTier;
}

export const PLAN_RANK: Record<PlanTier, number> = {
  starter: 0,
  pro: 1,
  team: 2,
};

/** Trial behaves as Starter for gating purposes. */
export const planRank = (planType: AccountPlanType | undefined): number =>
  planType === undefined || planType === "trial" ? 0 : PLAN_RANK[planType];

// Account / profile variables are left out, as in the backend copy: FREE_NAMES
// decides them first, so an entry for them could never apply.
const ACCOUNT_VARIABLE_NAMES: ReadonlySet<string> = new Set(
  accountVariables.map((v) => v.variable.name),
);

export const CATALOG_PLAN_BY_NAME: Readonly<Record<string, PlanTier>> =
  Object.fromEntries(
    STORYDOC_VARIABLES.filter((v) => !ACCOUNT_VARIABLE_NAMES.has(v.key)).map(
      (v) => [v.key, v.plan],
    ),
  );

const FREE_NAMES: ReadonlySet<string> = new Set([
  ...FAST_VERSION_VARIABLES.map((v) => v.variable.name),
  ...accountVariables.map((v) => v.variable.name),
]);

export const requiredPlanForVariable = (
  variable: PlanRuleVariable,
): PlanTier => {
  if (FREE_NAMES.has(variable.name)) return "starter";
  if (variable.name.startsWith("fathom.")) return "starter";
  const catalogued = CATALOG_PLAN_BY_NAME[variable.name];
  if (catalogued) return catalogued;
  if (variable.type === "calculation") return "team";
  return "pro";
};

/**
 * Variables (from the fs-vars manifest) the given account plan may not
 * publish, deduped by name, highest required plan first.
 */
export const findBlockedVariables = (
  variables: readonly PlanRuleVariable[],
  planType: AccountPlanType | undefined,
): BlockedVariable[] => {
  const rank = planRank(planType);
  const seen = new Set<string>();
  const blocked: BlockedVariable[] = [];
  for (const variable of variables) {
    if (!variable?.name || seen.has(variable.name)) continue;
    seen.add(variable.name);
    const requiredPlan = requiredPlanForVariable(variable);
    if (PLAN_RANK[requiredPlan] > rank) {
      blocked.push({ name: variable.name, requiredPlan });
    }
  }
  return blocked.sort(
    (a, b) =>
      PLAN_RANK[b.requiredPlan] - PLAN_RANK[a.requiredPlan] ||
      a.name.localeCompare(b.name),
  );
};

/** The single plan to upsell: the highest one any blocked variable needs. */
export const highestRequiredPlan = (
  blocked: readonly BlockedVariable[],
): PlanTier =>
  blocked.reduce<PlanTier>(
    (top, v) =>
      PLAN_RANK[v.requiredPlan] > PLAN_RANK[top] ? v.requiredPlan : top,
    "starter",
  );
